import {Button, Card} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {formatPhoneNumber} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {TelephonyCallDetail} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {customerRoutes} from '@dms/routes';

import {composePath, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

interface CallCustomerCardProps extends TestIdProps {
  call: TelephonyCallDetail;
}

/**
 * The call's customer with a way to the customer page; for an unknown number, the same two ways
 * forward as the call panel offers. Neither links the call to the customer picked or created.
 */
export function CallCustomerCard(props: CallCustomerCardProps) {
  const navigate = useNavigate();
  const customer = props.call.customer;
  const phoneNumber = isNotNil(props.call.phoneNumber)
    ? formatPhoneNumber(props.call.phoneNumber)
    : null;

  return (
    <Card
      title={i18n.t('general.labels.customer')}
      actions={
        isNotNil(customer)
          ? [
              {
                type: 'button',
                variant: 'link',
                size: 'small',
                leftIcon: 'action/open_in_new',
                title: i18n.t('page.telephony.indicator.openCustomer'),
                onClick: () =>
                  navigate(composePath(customerRoutes.detail, {params: {id: customer.id}})),
                'data-testid': suffixTestId('openCustomer', props),
              },
            ]
          : undefined
      }
      data-testid={suffixTestId('customerCard', props)}
    >
      <Show when={customer}>
        {(linkedCustomer) => (
          <VStack spacing={1}>
            <Text alternative data-testid={suffixTestId('customerName', props)}>
              {linkedCustomer.name ?? phoneNumber ?? ''}
            </Text>
            <Show
              when={isNotNil(linkedCustomer.name) && isNotNil(phoneNumber) ? phoneNumber : null}
            >
              {(number) => (
                <Text size="small" color="secondary">
                  {number}
                </Text>
              )}
            </Show>
          </VStack>
        )}
      </Show>
      <Show when={isNil(customer)}>
        <VStack spacing={3}>
          <Text size="small" color="secondary" data-testid={suffixTestId('noCustomer', props)}>
            {isNotNil(phoneNumber)
              ? i18n.t('page.telephony.indicator.unknownCaller')
              : i18n.t('page.telephony.callDetail.withheldNumber')}
          </Text>
          <Show when={isNotNil(phoneNumber)}>
            <HStack spacing={2} wrap>
              <Button
                variant="secondary"
                size="small"
                leftIcon="content/add"
                title={i18n.t('entity.customer.actions.addCustomer')}
                onClick={() => navigate(customerRoutes.create)}
                data-testid={suffixTestId('createCustomer', props)}
              />
              <Button
                variant="secondary"
                size="small"
                leftIcon="action/search"
                title={i18n.t('page.telephony.indicator.findCustomer')}
                onClick={() => navigate(customerRoutes.overview)}
                data-testid={suffixTestId('findCustomer', props)}
              />
            </HStack>
          </Show>
        </VStack>
      </Show>
    </Card>
  );
}
